/* eslint-disable camelcase */

import React, { useState, useCallback, useMemo, useEffect } from 'react'
import FileInput from '../../shared/FileInput'
import { RadioGroup } from '../../ui'
import { updateErrors } from '../SectionField'
import TrashCanIcon from '../../ui/icons/TrashCanIcon'
import DuplicateIcon from '../../ui/icons/DuplicateIcon'
import SelectField from '../BasicFields/SelectField'

const Pdf = ({
  question,
  data,
  name,
  dragHandleProps,
  handleQuestionDuplicate,
  handleQuestionRemove,
  handleQuestionChange,
  getErrors
}) => {
  const [state, setState] = useState({
    id: question.id,
    context: question.context,
    position: question.position,
    pdf: question.pdf,
    variable_id: question.variable_id,
    isDynamic: question.isDynamic || Boolean(question.variable_id),
    changed: question.changed || false,
    errors: question.errors || {}
  })

  const variables = useMemo(() => {
    if (data && data.variables)
      return data.variables.filter(variable => variable.variable_type === 'pdf')

    return []
  }, [data])

  const errors = useMemo(() => updateErrors(state.errors, getErrors), [state.errors, getErrors])

  useEffect(() => {
    handleQuestionChange(state)
  }, [state, handleQuestionChange])

  const handlePdfChange = useCallback(file => {
    setState(prevState => ({
      ...prevState,
      pdf: file,
      changed: true
    }))
  }, [])

  const handlePdfDelete = useCallback(() => {
    setState(prevState => ({
      ...prevState,
      pdf: null,
      changed: true
    }))
  }, [])

  const handleVariableChange = useCallback(event => {
    setState(prevState => ({
      ...prevState,
      variable_id: event.target.value,
      changed: true
    }))
  }, [])

  const handleSourceChange = useCallback(isDynamic => {
    const defaultVariableId = question.variable_id ||
      variables.length > 0 && variables[0].id || null

    let changed = true
    if (!isDynamic && state.pdf?.id)
      changed = false

    setState(prevState => ({
      ...prevState,
      variable_id: isDynamic ? defaultVariableId : null,
      isDynamic,
      changed
    }))
  }, [question.variable_id, variables, state.pdf])

  const pdfVariableOptions = useMemo(() =>
    variables.map(variable => ({
      id: variable.id,
      name: variable.name
    })), [variables])

  const sourceOptions = useMemo(() => [
    {
      id: `pdf_static_${state.id}`,
      value: false,
      label: 'PDF unique'
    },
    {
      id: `pdf_dynamic_${state.id}`,
      value: true,
      label: 'Variable PDF'
    }
  ], [state.id])

  const handleDuplicate = useCallback(() => {
    handleQuestionDuplicate(state.id)()
  }, [handleQuestionDuplicate, state.id])

  const handleRemove = useCallback(() => {
    handleQuestionRemove(state.id)()
  }, [handleQuestionRemove, state.id])

  return (
    <div className="nested-fields ml-2 ml-md-4">
      <div className="d-flex">
        <div className="mr-auto">
          <i className="fas fa-arrows-alt c-pointer" {...dragHandleProps} />
          {' PDF '}
          <DuplicateIcon
            className="tw:text-gray-500 tw:hover:text-gray-700 c-pointer"
            onClick={handleDuplicate}
          />
        </div>
        <TrashCanIcon
          className="tw:text-gray-500 tw:hover:text-gray-700 c-pointer"
          onClick={handleRemove}
        />
      </div>
      <RadioGroup
        name={`pdf_source_${state.id}`}
        options={sourceOptions}
        value={state.isDynamic}
        label="Source du PDF"
        inline
        onChange={handleSourceChange}
      />
      {state.isDynamic ? (
        <>
          <SelectField
            name={`${name}.variable_id`}
            label="Variable PDF"
            data={pdfVariableOptions}
            value={state.variable_id}
            errors={errors.variable}
            includeBlank={false}
            onChange={handleVariableChange}
          />
          {variables.length === 0 && (
            <div className="text-muted small mt-1">
              Aucune variable PDF disponible. Créez d&apos;abord une variable de type PDF.
            </div>
          )}
        </>
      ) : (
        <div className="form-group">
          <label>Sélectionnez votre fichier PDF :</label>
          <FileInput
            name="pdf"
            value={state.pdf?.filename || state.pdf?.name || ''}
            accept=".pdf"
            placeholder="Sélectionner un fichier PDF"
            errors={errors.pdf}
            allowDelete={false}
            onChange={handlePdfChange}
            onDelete={handlePdfDelete}
          />
        </div>
      )}
    </div>
  )
}

export default Pdf